import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import { useI18n } from "@/lib/i18n/useI18n";
import useRequest from "@/lib/request/useRequest";
import { roundTo } from "@/lib/utils/numbers";
import { sortArrayByKey } from "@/lib/utils/sorting";
import {
    CropWaterUse,
    ProbeSimulation,
    WaterReceived,
    WaterRisks,
} from "@/types/water";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import {
    Bar,
    BarChart,
    ResponsiveContainer,
    YAxis,
    XAxis,
    Tooltip,
    Legend,
    Line,
    LineChart,
} from "recharts";
import { scaleTime } from "d3-scale";
import { useTranslation } from "react-i18next";
import useResizeObserver from "@/lib/utils/useResizeObserver";
import { DateValue, RangeValue, Skeleton, Button } from "@heroui/react";
import useAuth from "@/lib/auth/useAuth";
import { Resource } from "@/types/user";
import naveLogo from '@/assets/images/Nave-Transparent-Logo.avif'
import { Link } from "@tanstack/react-router";
import PrecipitationEdit from "./PrecipitationEdit";
import SoilWaterContent from "./SoilWaterContent";
import hydroFlowHorizontal from "@/assets/images/HydroFlow_Horizontal_TM.png";

function mmToIn(mm: number) {
    return mm * 0.0393701;
}

function WaterCharts({ dateRange }: { dateRange: RangeValue<DateValue> | null }) {
    const { useGetRequest } = useRequest();
    const { fieldId, fieldHasIrrigation } = useFieldDashboard() ?? {};
    const { formatDateMMDDYYYY, formatUnit, isImperial } = useI18n();
    const { t } = useTranslation();

    const waterReceivedQuery = useGetRequest<WaterReceived>({
        url: `fields/${fieldId}/water_received/`,
    });

    const waterReceivedData = useMemo(() => {
        return sortArrayByKey(
            waterReceivedQuery.data?.data
                ?.filter(
                    (d) =>
                        dayjs(d.time).isSameOrAfter(dateRange?.start?.toString()) &&
                        dayjs(d.time).isSameOrBefore(dateRange?.end?.toString()),
                )
                .map((d) => ({
                    ...d,
                    time: dayjs(d.time).toDate().getTime(),
                })) ?? [],
            "time",
        );
    }, [waterReceivedQuery.data?.data, dateRange]);

    const totalPrecipitation = useMemo(() => {
        return roundTo(
            waterReceivedData.reduce(
                (acc, curr) => acc + curr.precipitation,
                0,
            ),
            2,
        );
    }, [waterReceivedData]);
    const totalIrrigation = useMemo(() => {
        return roundTo(
            waterReceivedData.reduce((acc, curr) => acc + curr.irrigation, 0),
            2,
        );
    }, [waterReceivedData]);

    const probeSimulationQuery = useGetRequest<ProbeSimulation>({
        url: `fields/${fieldId}/probe_simulation/`,
    });

    const probeSimulationData = useMemo(() => {
        return sortArrayByKey(
            probeSimulationQuery.data?.data
                ?.filter(
                    (d) =>
                        dayjs(d.time).isSameOrAfter(dateRange?.start?.toString()) &&
                        dayjs(d.time).isSameOrBefore(dateRange?.end?.toString()),
                )
                .map((d) => ({
                    ...d,
                    time: dayjs(d.time).toDate().getTime(),
                })) ?? [],
            "time",
        );
    }, [probeSimulationQuery.data?.data, dateRange]);

    const probeSimulationColorKey = useMemo(() => {
        return {
            swc_005cm: {
                color: "#4F8DFD",
                name: `Soil Water Content ${isImperial ? roundTo(mmToIn(50), 1) : 50} in`,
            },
            swc_015cm: {
                color: "#42C68E",
                name: `Soil Water Content ${isImperial ? roundTo(mmToIn(150), 1) : 150} in`,
            },
            swc_030cm: {
                color: "#FFD16F",
                name: `Soil Water Content ${isImperial ? roundTo(mmToIn(300), 1) : 300} in`,
            },
            swc_060cm: {
                color: "#F96C77",
                name: `Soil Water Content ${isImperial ? roundTo(mmToIn(600), 1) : 600} in`,
            },
        };
    }, [isImperial]);

    const cropWaterUseQuery = useGetRequest<CropWaterUse>({
        url: `fields/${fieldId}/crop_water_use/`,
    });

    const cropWaterUseData = useMemo(() => {
        return sortArrayByKey(
            cropWaterUseQuery.data?.data
                ?.filter(
                    (d) =>
                        dayjs(d.time).isSameOrAfter(dateRange?.start?.toString()) &&
                        dayjs(d.time).isSameOrBefore(dateRange?.end?.toString()),
                )
                .map((d) => ({
                    ...d,
                    range: [d.et_under, d.et_over],
                    replacement: isImperial ? 0.3 : 7.62,
                    time: dayjs(d.time).toDate().getTime(),
                })) ?? [],
            "time",
        );
    }, [cropWaterUseQuery.data?.data, isImperial, dateRange]);

    const { isAtLeastDetail } = useAuth();

    const waterRisksQuery = useGetRequest<WaterRisks>({
        url: `fields/${fieldId}/water_risks/`,
        enabled: isAtLeastDetail(Resource.IRRIGATION),
    });

    const waterRisksData = useMemo(() => {
        return sortArrayByKey(
            waterRisksQuery.data?.data
                ?.filter(
                    (d) =>
                        dayjs(d.time).isSameOrAfter(dateRange?.start?.toString()) &&
                        dayjs(d.time).isSameOrBefore(dateRange?.end?.toString()),
                )
                .map((d) => ({
                    ...d,
                    time: dayjs(d.time).toDate().getTime(),
                })) ?? [],
            "time",
        );
    }, [waterRisksQuery.data?.data, dateRange]);

    const maxValue = Math.max(
        Math.ceil(
            Math.max(...waterReceivedData.map((d) => d.time as number)) / 2,
        ) * 2,
    );
    const minValue = Math.min(
        ...waterReceivedData.map((d) => d.time as number),
    );
    const timeScale = scaleTime().domain([minValue, maxValue]);

    const domain = timeScale.domain().map((d) => d.getTime());
    const ticks = timeScale.ticks(5).map((d) => d.getTime());

    const { elementRef, isResizing } = useResizeObserver(250);

    const [showEditPrecipitationModal, setShowEditPrecipitationModal] = useState(false);

    return (
        <div className="w-full h-[100rem]  " ref={elementRef}>
            {isResizing ? (
                <Skeleton className="h-full w-full" />
            ) : (
                <div className="flex flex-col gap-3">
                    <div className="flex flex-row items-center gap-4 justify-center">
                        <img src={hydroFlowHorizontal} alt="HydroFlow" className="h-6 w-auto" />
                        <div className="flex flex-row items-center gap-2 text-sm font-bold">Powered by <img src={naveLogo} alt="Nave" className="h-6 w-auto" /></div>
                    </div>
                    {!fieldHasIrrigation ? (
                        <div>
                            <div className="text-sm italic text-center mt-4">Enroll in Irrigation to view this data</div>
                        </div>
                    ) : (
                        <>
                            <SoilWaterContent fieldId={fieldId} dateRange={dateRange} />
                            <div>
                                <div className="flex flex-row items-center gap-1 justify-between">
                                    <div className="font-semibold">Water Received</div>
                                    <div className="flex flex-row items-center gap-1">
                                        <Button
                                            size="sm"
                                            radius="sm"
                                            className="bg-[#8884d8] text-white"
                                            as={Link}
                                            to={`/dashboard/fields/${fieldId}/operations`}
                                            target="_blank"
                                        >
                                            Total Irrigation: {totalIrrigation}{" "}
                                            {formatUnit("in", "mm")}
                                        </Button>
                                        <Button
                                            size="sm"
                                            radius="sm"
                                            className="bg-[#82ca9d] text-white"
                                            onPress={() => setShowEditPrecipitationModal(true)}
                                        >
                                            Total Precipitation: {totalPrecipitation}{" "}
                                            {formatUnit("in", "mm")}
                                        </Button>
                                    </div>
                                </div>
                                {waterReceivedData.length ? (
                                    <ResponsiveContainer width="100%" height={250}>
                                        <BarChart data={waterReceivedData} syncId="water">
                                            <XAxis
                                                dataKey="time"
                                                scale="linear"
                                                type="number"
                                                spacing={50}
                                                angle={-8}
                                                fontSize={12}
                                                tickFormatter={(t) => formatDateMMDDYYYY(t)}
                                                domain={domain}
                                                ticks={ticks}
                                            />
                                            <YAxis
                                                fontSize={12}
                                                label={{
                                                    value: t("chartLabels.waterReceived", {
                                                        ns: "insights",
                                                    }),
                                                    angle: -90,
                                                    dx: -20,
                                                }}
                                            />
                                            <Bar
                                                dataKey="irrigation"
                                                fill="#8884d8"
                                                barSize={10}
                                                name={t("chartLabels.irrigation", {
                                                    ns: "insights",
                                                })}
                                            />
                                            <Bar
                                                dataKey="precipitation"
                                                fill="#82ca9d"
                                                barSize={10}
                                                name={t("chartLabels.precipitation", {
                                                    ns: "insights",
                                                })}
                                                onClick={() => {
                                                    setShowEditPrecipitationModal(true);
                                                }}
                                            />
                                            <Tooltip
                                                labelFormatter={(t) =>
                                                    formatDateMMDDYYYY(t)
                                                }
                                                formatter={(value, name) => [
                                                    `${roundTo(value as number, 1)} ${formatUnit("in", "mm")}`,
                                                    name,
                                                ]}
                                            />
                                            <Legend
                                                verticalAlign="top"
                                                height={36}
                                                fontSize={12}
                                                wrapperStyle={{ fontSize: "12px" }}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div className="text-sm italic text-center mt-4">No data available</div>
                                )}
                            </div>
                            <div className="font-semibold">Probe Simulation</div>
                            {probeSimulationData.length ? (
                                <ResponsiveContainer width="100%" height={250}>
                                    <LineChart data={probeSimulationData} syncId="water">
                                        <XAxis
                                            dataKey="time"
                                            scale="linear"
                                            type="number"
                                            spacing={50}
                                            angle={-8}
                                            fontSize={12}
                                            tickFormatter={(t) => formatDateMMDDYYYY(t)}
                                            domain={domain}
                                            ticks={ticks}
                                        />
                                        <YAxis
                                            fontSize={12}
                                            label={{
                                                value: `${t("chartLabels.probeSimulation", {
                                                    ns: "insights",
                                                })} ${formatUnit("in", "mm")}`,
                                                angle: -90,
                                                dx: -20,
                                            }}
                                        />
                                        {Object.entries(probeSimulationColorKey).map(
                                            ([key, value]) => (
                                                <Line
                                                    dataKey={key}
                                                    stroke={value.color}
                                                    dot={false}
                                                    strokeWidth={1}
                                                    name={value.name}
                                                />
                                            ),
                                        )}

                                        <Tooltip
                                            labelFormatter={(t) => formatDateMMDDYYYY(t)}
                                            formatter={(value, name) => [
                                                `${roundTo(value as number, 1)} ${formatUnit("in", "mm")}`,
                                                name,
                                            ]}
                                        />
                                        <Legend
                                            verticalAlign="top"
                                            height={36}
                                            fontSize={12}
                                            wrapperStyle={{ fontSize: "12px" }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="text-sm italic text-center mt-4">No data available</div>
                            )}
                            <div className="font-semibold">Crop Water Use</div>
                            {cropWaterUseData.length ? (<ResponsiveContainer width="100%" height={250}>
                                <LineChart data={cropWaterUseData} syncId="water">
                                    <XAxis
                                        dataKey="time"
                                        scale="linear"
                                        type="number"
                                        spacing={50}
                                        angle={-8}
                                        fontSize={12}
                                        tickFormatter={(t) => formatDateMMDDYYYY(t)}
                                        domain={domain}
                                        ticks={ticks}
                                    />
                                    <YAxis
                                        fontSize={12}
                                        label={{
                                            value: t("chartLabels.cropWaterUse", {
                                                ns: "insights",
                                            }),
                                            angle: -90,
                                            dx: -20,
                                        }}
                                    />
                                    <Line
                                        dataKey="et_estimate"
                                        stroke="orange"
                                        dot={false}
                                        strokeWidth={1}
                                        name={t("chartLabels.etEstimate", {
                                            ns: "insights",
                                        })}
                                    />
                                    <Line
                                        dataKey="range"
                                        stroke="orange"
                                        dot={false}
                                        strokeWidth={1}
                                        opacity={0.5}
                                        legendType="none"
                                        tooltipType="none"
                                    />
                                    <Line
                                        dataKey="replacement"
                                        stroke="black"
                                        strokeDasharray="5 5"
                                        dot={false}
                                        strokeWidth={1}
                                        tooltipType="none"
                                        name={t("chartLabels.replacement", {
                                            ns: "insights",
                                        })}
                                    />
                                    <Tooltip
                                        labelFormatter={(t) => formatDateMMDDYYYY(t)}
                                        formatter={(value, name) => [
                                            `${roundTo(value as number, 1)} ${formatUnit("in", "mm")}`,
                                            name,
                                        ]}
                                    />
                                    <Legend
                                        verticalAlign="top"
                                        height={36}
                                        fontSize={12}
                                        wrapperStyle={{ fontSize: "12px" }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>) : (
                                <div>
                                    <div className="text-sm italic text-center mt-4">No data available</div>
                                </div>
                            )}
                            <div className="font-semibold">Water Risks</div>
                            {isAtLeastDetail(Resource.IRRIGATION) && waterRisksData.length ? (
                                <ResponsiveContainer width="100%" height={250}>
                                    <LineChart data={waterRisksData} syncId="water">
                                        <XAxis
                                            dataKey="time"
                                            scale="linear"
                                            type="number"
                                            spacing={50}
                                            angle={-8}
                                            fontSize={12}
                                            tickFormatter={(t) => formatDateMMDDYYYY(t)}
                                            domain={domain}
                                            ticks={ticks}
                                        />
                                        <YAxis
                                            fontSize={12}
                                            label={{
                                                value: "Risk %",
                                                angle: -90,
                                                dx: -20,
                                            }}
                                            domain={[0, 100]}
                                            allowDataOverflow
                                            type="number"
                                            scale="linear"
                                        />
                                        <Line
                                            dataKey="leach_risk"
                                            stroke="blue"
                                            dot={false}
                                            strokeWidth={1}
                                            name={t("chartLabels.leachingRisk", {
                                                ns: "insights",
                                            })}
                                            legendType="line"
                                        />
                                        <Line
                                            dataKey="water_risk"
                                            stroke="orange"
                                            dot={false}
                                            strokeWidth={1}
                                            name={t("chartLabels.waterRisk", {
                                                ns: "insights",
                                            })}
                                            legendType="line"
                                        />
                                        <Tooltip
                                            labelFormatter={(t) =>
                                                formatDateMMDDYYYY(t)
                                            }
                                            formatter={(value, name) => [
                                                `${roundTo(value as number, 1)} %`,
                                                name,
                                            ]}
                                        />
                                        <Legend
                                            verticalAlign="top"
                                            height={36}
                                            fontSize={12}
                                            wrapperStyle={{ fontSize: "12px" }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : isAtLeastDetail(Resource.IRRIGATION) ? (
                                <div>
                                    <div className="text-sm italic text-center mt-4">No data available</div>
                                </div>
                            ) : null
                            }
                            {showEditPrecipitationModal && (
                                <PrecipitationEdit
                                    precipitationData={waterReceivedData.map((d) => ({
                                        field: fieldId!,
                                        date: dayjs(d.time).format("YYYY-MM-DD"),
                                        precipitation: roundTo(d.precipitation, 5),
                                    }))}
                                    onClose={() => setShowEditPrecipitationModal(false)}
                                />
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export default WaterCharts;
